/*
 * MN Testimonials slider — lightweight track slider.
 * Supports autoplay, seamless loop, keyboard, pointer swipe/drag, Theme Editor reload.
 */
class MnTestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.index = 0;
    this.slides = [];
    this.realSlideCount = 0;
    this.loopOffset = 0;
    this.track = null;
    this.viewport = null;
    this.prevButton = null;
    this.nextButton = null;
    this.autoplayTimer = null;
    this.loopSnapTimer = null;
    this.loopSnapHandler = null;
    this.resizeObserver = null;
    this.abortController = null;
    this.reduceMotion = null;
    this.hoverMedia = null;
    this.isPointerInside = false;
    this.pointerId = null;
    this.dragStartX = 0;
    this.dragDeltaX = 0;
    this.isDragging = false;
    this.dragMoved = false;

    this.goTo = this.goTo.bind(this);
    this.next = this.next.bind(this);
    this.prev = this.prev.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleVisibility = this.handleVisibility.bind(this);
    this.update = this.update.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
  }

  connectedCallback() {
    this.cleanup();
    this.abortController = new AbortController();
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');

    this.track = this.querySelector('[data-mn-testimonials-track]');
    this.viewport = this.querySelector('.mn-testimonials-slider__viewport') || this.track?.parentElement;
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.removeLoopClones();
    this.slides = [...this.querySelectorAll('[data-mn-testimonials-slide]')];

    if (!(this.track instanceof HTMLElement) || this.slides.length === 0) return;

    this.setupLoopClones();

    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', this.prev, { signal });
    this.nextButton?.addEventListener('click', this.next, { signal });
    this.addEventListener('keydown', this.handleKeydown, { signal });
    document.addEventListener('visibilitychange', this.handleVisibility, { signal });

    const dragTarget = this.viewport instanceof HTMLElement ? this.viewport : this;
    dragTarget.addEventListener('pointerdown', this.onPointerDown, { signal });
    dragTarget.addEventListener('pointermove', this.onPointerMove, { signal });
    dragTarget.addEventListener('pointerup', this.onPointerUp, { signal });
    dragTarget.addEventListener('pointercancel', this.onPointerUp, { signal });
    dragTarget.addEventListener('lostpointercapture', this.onPointerUp, { signal });
    dragTarget.addEventListener('click', this.onClickCapture, { capture: true, signal });

    // Hover-pause only on devices that truly hover. Touch pointerenter without
    // a matching leave used to leave autoplay stopped forever.
    this.addEventListener(
      'pointerenter',
      (event) => {
        if (!this.canHoverPause || event.pointerType === 'touch') return;
        this.isPointerInside = true;
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerType === 'touch') return;
        this.isPointerInside = false;
        if (!this.isDragging) this.startAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      () => {
        this.stopAutoplay();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (this.contains(event.relatedTarget)) return;
        // Defer so button click handlers can finish first.
        window.requestAnimationFrame(() => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });
      },
      { signal }
    );

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.update, { signal });
    }

    this.setAttribute('tabindex', '0');
    this.syncArrowVisibility();
    this.goTo(this.loopOffset, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  get canHoverPause() {
    return this.hoverMedia?.matches === true;
  }

  get autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.realSlideCount > 1;
  }

  get autoplaySpeed() {
    const speed = Number.parseInt(this.dataset.autoplaySpeed || '5000', 10);
    return Number.isFinite(speed) && speed >= 1000 ? speed : 5000;
  }

  get loopEnabled() {
    return this.dataset.loop === 'true' && this.realSlideCount > 1;
  }

  get showArrows() {
    return this.dataset.showArrows !== 'false' && this.realSlideCount > 1;
  }

  get transitionMs() {
    const duration = Number.parseInt(this.dataset.transition || '500', 10);
    return Number.isFinite(duration) && duration >= 0 ? duration : 500;
  }

  removeLoopClones() {
    if (!(this.track instanceof HTMLElement)) return;
    this.track.querySelectorAll('[data-mn-testimonials-clone]').forEach((node) => node.remove());
  }

  setupLoopClones() {
    this.realSlideCount = this.slides.length;
    this.loopOffset = 0;

    if (!this.loopEnabled || !(this.track instanceof HTMLElement)) return;

    const originals = this.slides.slice();
    const beforeFragment = document.createDocumentFragment();
    const afterFragment = document.createDocumentFragment();

    originals.forEach((slide) => {
      const before = slide.cloneNode(true);
      before.setAttribute('data-mn-testimonials-clone', 'before');
      before.setAttribute('aria-hidden', 'true');
      before.removeAttribute('id');
      before.removeAttribute('data-active');
      beforeFragment.appendChild(before);

      const after = slide.cloneNode(true);
      after.setAttribute('data-mn-testimonials-clone', 'after');
      after.setAttribute('aria-hidden', 'true');
      after.removeAttribute('id');
      after.removeAttribute('data-active');
      afterFragment.appendChild(after);
    });

    this.track.prepend(beforeFragment);
    this.track.append(afterFragment);

    this.slides = [...this.querySelectorAll('[data-mn-testimonials-slide]')];
    this.loopOffset = this.realSlideCount;
  }

  logicalIndex() {
    if (this.realSlideCount <= 0) return 0;
    if (!this.loopEnabled) return this.index;
    const relative = this.index - this.loopOffset;
    return ((relative % this.realSlideCount) + this.realSlideCount) % this.realSlideCount;
  }

  clearLoopSnap() {
    if (this.loopSnapHandler && this.track instanceof HTMLElement) {
      this.track.removeEventListener('transitionend', this.loopSnapHandler);
    }
    this.loopSnapHandler = null;
    if (this.loopSnapTimer !== null) {
      window.clearTimeout(this.loopSnapTimer);
      this.loopSnapTimer = null;
    }
  }

  snapLoopPosition() {
    this.clearLoopSnap();
    if (!this.loopEnabled || this.realSlideCount <= 0) return;

    const normalized = this.loopOffset + this.logicalIndex();
    if (normalized === this.index) return;

    this.index = normalized;
    this.setTrackOffset(this.baseOffset(), false);
    // Force reflow so the next animated move does not inherit the jump.
    void this.track.offsetWidth;
    this.syncSlideState();
  }

  queueLoopSnap() {
    this.clearLoopSnap();
    if (!(this.track instanceof HTMLElement)) return;

    this.loopSnapHandler = (event) => {
      if (event.target !== this.track || event.propertyName !== 'transform') return;
      this.snapLoopPosition();
    };
    this.track.addEventListener('transitionend', this.loopSnapHandler);
    this.loopSnapTimer = window.setTimeout(() => this.snapLoopPosition(), this.transitionMs + 80);
  }

  syncArrowVisibility() {
    const visible = this.showArrows;
    [this.prevButton, this.nextButton].forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      button.hidden = !visible;
    });
  }

  updateArrowState() {
    if (!(this.prevButton instanceof HTMLButtonElement) || !(this.nextButton instanceof HTMLButtonElement)) {
      return;
    }

    if (this.loopEnabled || this.realSlideCount <= 1) {
      this.prevButton.disabled = this.realSlideCount <= 1;
      this.nextButton.disabled = this.realSlideCount <= 1;
      return;
    }

    this.prevButton.disabled = this.index <= 0;
    this.nextButton.disabled = this.index >= this.realSlideCount - 1;
  }

  syncSlideState() {
    const logical = this.logicalIndex();
    this.slides.forEach((slide, slideIndex) => {
      const isClone = slide.hasAttribute('data-mn-testimonials-clone');
      const active = !isClone && slideIndex === this.loopOffset + logical;
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('data-active', active);
    });
    this.updateArrowState();
  }

  slideStep() {
    const first = this.slides[0];
    if (!(first instanceof HTMLElement)) return 0;
    const styles = window.getComputedStyle(this.track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return first.getBoundingClientRect().width + gap;
  }

  baseOffset() {
    return this.slideStep() * this.index;
  }

  setTrackOffset(offset, animate = true) {
    if (!(this.track instanceof HTMLElement)) return;
    this.track.style.transitionDuration = animate && !this.reduceMotion?.matches ? `${this.transitionMs}ms` : '0ms';
    this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
  }

  goTo(index, animate = true) {
    if (this.slides.length === 0 || this.realSlideCount === 0) return;

    let nextIndex = index;
    if (this.loopEnabled) {
      nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    } else {
      nextIndex = Math.max(0, Math.min(index, this.realSlideCount - 1));
    }

    this.index = nextIndex;
    this.setTrackOffset(this.baseOffset(), animate);
    this.syncSlideState();

    if (this.loopEnabled && animate && !this.reduceMotion?.matches && this.transitionMs > 0) {
      this.queueLoopSnap();
    } else if (this.loopEnabled) {
      this.snapLoopPosition();
    }
  }

  next() {
    if (this.loopEnabled) {
      this.goTo(this.index + 1);
    } else if (this.index < this.realSlideCount - 1) {
      this.goTo(this.index + 1);
    }
    this.restartAutoplay();
  }

  prev() {
    if (this.loopEnabled) {
      this.goTo(this.index - 1);
    } else if (this.index > 0) {
      this.goTo(this.index - 1);
    }
    this.restartAutoplay();
  }

  update() {
    this.goTo(this.index, false);
  }

  onPointerDown(event) {
    if (this.realSlideCount <= 1) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button, a')) return;

    // Block native image / text drag so pointer capture stays on the slider.
    event.preventDefault();

    this.clearLoopSnap();
    this.snapLoopPosition();

    this.isDragging = true;
    this.dragMoved = false;
    this.pointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragDeltaX = 0;
    this.stopAutoplay();

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    target.setPointerCapture?.(event.pointerId);
    this.setTrackOffset(this.baseOffset(), false);
    target.classList.add('is-dragging');
  }

  onPointerMove(event) {
    if (!this.isDragging || event.pointerId !== this.pointerId) return;

    this.dragDeltaX = event.clientX - this.dragStartX;
    if (Math.abs(this.dragDeltaX) > 6) this.dragMoved = true;

    let offset = this.baseOffset() - this.dragDeltaX;
    if (!this.loopEnabled) {
      const max = this.slideStep() * (this.realSlideCount - 1);
      offset = Math.max(0, Math.min(offset, max));
    }

    this.setTrackOffset(offset, false);
  }

  onPointerUp(event) {
    if (!this.isDragging) return;
    if (this.pointerId !== null && event.pointerId !== this.pointerId) return;

    const target = this.viewport instanceof HTMLElement ? this.viewport : this;
    if (this.pointerId !== null) {
      try {
        target.releasePointerCapture?.(this.pointerId);
      } catch (error) {
        // Capture may already be released (lostpointercapture).
      }
    }
    target.classList.remove('is-dragging');

    const step = this.slideStep();
    const threshold = Math.min(80, step * 0.22);
    const delta = this.dragDeltaX;

    this.isDragging = false;
    this.pointerId = null;
    this.dragDeltaX = 0;

    // Touch never reliably fires pointerleave — clear stuck hover pause.
    if (event.pointerType === 'touch' || !this.canHoverPause) {
      this.isPointerInside = false;
    }

    if (Math.abs(delta) >= threshold) {
      if (delta < 0) this.next();
      else this.prev();
    } else {
      this.goTo(this.index, true);
      this.restartAutoplay();
    }
  }

  onClickCapture(event) {
    if (!this.dragMoved) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragMoved = false;
  }

  handleKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  }

  handleVisibility() {
    if (document.hidden) {
      this.stopAutoplay();
    } else if (!this.isDragging) {
      this.startAutoplay();
    }
  }

  startAutoplay() {
    this.stopAutoplay();
    if (
      !this.autoplayEnabled ||
      this.reduceMotion?.matches ||
      document.hidden ||
      this.isDragging
    ) {
      return;
    }

    // Only honor hover-pause on real hover devices.
    if (this.canHoverPause && this.isPointerInside) return;

    this.autoplayTimer = window.setInterval(() => {
      if (this.loopEnabled) {
        this.goTo(this.index + 1);
      } else if (this.index >= this.realSlideCount - 1) {
        this.goTo(0);
      } else {
        this.goTo(this.index + 1);
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer !== null) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    // Avoid restarting while the cursor is still hovering (desktop pause).
    if (this.canHoverPause && this.isPointerInside) return;
    this.startAutoplay();
  }

  cleanup() {
    this.stopAutoplay();
    this.clearLoopSnap();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.abortController?.abort();
    this.abortController = null;
    this.isDragging = false;
    this.isPointerInside = false;
    this.pointerId = null;
  }
}

if (!customElements.get('mn-testimonials-slider')) {
  customElements.define('mn-testimonials-slider', MnTestimonialsSlider);
}

document.addEventListener('shopify:section:load', (event) => {
  const root = event.target;
  if (!(root instanceof Element)) return;
  root.querySelectorAll('mn-testimonials-slider').forEach((el) => {
    if (el instanceof MnTestimonialsSlider && el.isConnected) {
      el.connectedCallback();
    }
  });
});
